import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { head } from "ramda"
import {
    ADD_SCHEMATIZED_GRID,
    ADD_SCHEMATIZED_LAYOUT,
    ADD_SCHEMATIZED_TILE,
    DELETE_SCHEMATIZED_GRID,
    DELETE_SCHEMATIZED_GRID_GROUP,
    DELETE_SCHEMATIZED_LAYOUT,
    DELETE_SCHEMATIZED_TILE,
    FETCH_SCHEMATIZED_DATA_REQUESTED,
    FETCH_SCHEMATIZED_SUCCESS,
    RESET_SCHEMATIZED_STATE,
    SCHEMATIZED_CANCEL_BUTTON_CLICKED,
    SCHEMATIZED_GRID_SELECTED,
    SCHEMATIZED_LAYOUT_RENAME_RESET,
    SCHEMATIZED_LAYOUT_RENAME_VALUE_CHANGED,
    SCHEMATIZED_LAYOUT_SELECTED,
    SCHEMATIZED_LAYOUTS_RESORTED,
    SCHEMATIZED_RESOLUTION_CHANGED,
    SCHEMATIZED_SAVE_SUCCESS,
    SCHEMATIZED_SEARCH_CLICKED,
    UPDATE_SCHEMATIZED_GRID_GROUP,
    UPDATE_SCHEMATIZED_GROUPS,
    UPDATE_SCHEMATIZED_LAYOUT,
    UPDATE_SCHEMATIZED_TILE,
} from "./actions"
import type { SchematizedData } from "../../../../graphql/types"
import { SchematizedSize } from "../../../../graphql/types"
import { getFirstLayoutId } from "./utils"

export type SchematizedState = {
    selectedLayoutId: Maybe<string>
    selectedGridId: Maybe<string>
    size: SchematizedSize
    sizeChanged: boolean
    searchKey: Maybe<string>
    oldSchematizedLayouts: SchematizedData[]
    schematizedLayouts: SchematizedData[]
    layoutRenameInputValue: Maybe<string>
}

export const createDefaultSchematizedState = (): SchematizedState => ({
    selectedLayoutId: null,
    selectedGridId: null,
    sizeChanged: false,
    size: SchematizedSize.Medium,
    searchKey: null,
    oldSchematizedLayouts: [],
    schematizedLayouts: [],
    layoutRenameInputValue: null,
})

export const schematizedReducer = (
    state: Maybe<SchematizedState> = undefined,
    action: Record<string, any>
): SchematizedState => {
    if (!state) {
        return createDefaultSchematizedState()
    }

    switch (action.type) {
        case FETCH_SCHEMATIZED_SUCCESS: {
            return {
                ...state,
                oldSchematizedLayouts: action.payload.schematizedLayouts,
                schematizedLayouts: action.payload.schematizedLayouts,
                selectedLayoutId: getFirstLayoutId(action.payload.schematizedLayouts),
                size: getFirstSelectedLayoutSize(action.payload.schematizedLayouts),
            }
        }

        // Tile actions
        case ADD_SCHEMATIZED_TILE: {
            return {
                ...state,
                schematizedLayouts: updateLayoutById(state.selectedLayoutId, state.schematizedLayouts, l => ({
                    ...l,
                    data: l.data.map(g => ({
                        ...g,
                        pages: g.pages.map(p => {
                            if (p.id === action.payload.gridId) {
                                return { ...p, tiles: [...p.tiles, action.payload.tileData] }
                            }

                            return p
                        }),
                    })),
                })),
            }
        }

        case UPDATE_SCHEMATIZED_TILE: {
            return {
                ...state,
                schematizedLayouts: updateLayoutById(state.selectedLayoutId, state.schematizedLayouts, l => ({
                    ...l,
                    data: l.data.map(g => ({
                        ...g,
                        pages: g.pages.map(p => ({
                            ...p,
                            tiles: p.tiles.map(t => {
                                if (t.id === action.payload.tileId) {
                                    return { ...t, ...action.payload.tileData }
                                }

                                return t
                            }),
                        })),
                    })),
                })),
            }
        }

        case DELETE_SCHEMATIZED_TILE: {
            return {
                ...state,
                schematizedLayouts: updateLayoutById(state.selectedLayoutId, state.schematizedLayouts, l => ({
                    ...l,
                    data: l.data.map(g => ({
                        ...g,
                        pages: g.pages.map(p => ({ ...p, tiles: p.tiles.filter(t => t.id !== action.payload.tileId) })),
                    })),
                })),
            }
        }

        // Grid/page actions
        case ADD_SCHEMATIZED_GRID: {
            return {
                ...state,
                schematizedLayouts: updateLayoutById(state.selectedLayoutId, state.schematizedLayouts, l => ({
                    ...l,
                    data: l.data.map(g => {
                        if (g.id === action.payload.groupId) {
                            return { ...g, pages: [...g.pages, action.payload.gridData] }
                        }

                        return g
                    }),
                })),
            }
        }

        case DELETE_SCHEMATIZED_GRID: {
            return {
                ...state,
                schematizedLayouts: updateLayoutById(state.selectedLayoutId, state.schematizedLayouts, l => ({
                    ...l,
                    data: l.data.map(g => ({ ...g, pages: g.pages.filter(p => p.id !== action.payload.gridId) })),
                })),
            }
        }

        // Grid group actions
        case UPDATE_SCHEMATIZED_GRID_GROUP: {
            return {
                ...state,
                schematizedLayouts: updateLayoutById(state.selectedLayoutId, state.schematizedLayouts, l => ({
                    ...l,
                    data: l.data.map(g => {
                        if (g.id === action.payload.groupId) {
                            return { ...g, ...action.payload.groupData }
                        }

                        return g
                    }),
                })),
            }
        }

        case DELETE_SCHEMATIZED_GRID_GROUP: {
            return {
                ...state,
                schematizedLayouts: updateLayoutById(state.selectedLayoutId, state.schematizedLayouts, l => ({
                    ...l,
                    data: l.data.filter(g => g.id !== action.payload.groupId),
                })),
            }
        }

        case UPDATE_SCHEMATIZED_GROUPS: {
            return {
                ...state,
                schematizedLayouts: updateLayoutById(state.selectedLayoutId, state.schematizedLayouts, l => ({
                    ...l,
                    data: action.payload.newSchematizedGroups,
                })),
            }
        }

        // Layouts/tabs actions
        case ADD_SCHEMATIZED_LAYOUT:
            return { ...state, schematizedLayouts: [...(state.schematizedLayouts || []), action.payload.layout] }

        case UPDATE_SCHEMATIZED_LAYOUT:
            return {
                ...state,
                schematizedLayouts: state.schematizedLayouts.map(l => {
                    if (l.layoutId === action.payload.layoutId) {
                        return { ...l, ...action.payload.layoutData }
                    }

                    return l
                }),
            }

        case DELETE_SCHEMATIZED_LAYOUT:
            return {
                ...state,
                schematizedLayouts: state.schematizedLayouts.filter(l => l.layoutId !== action.payload.layoutId),
            }

        case SCHEMATIZED_LAYOUTS_RESORTED:
            return {
                ...state,
                schematizedLayouts: reorderLayoutsSortIndex(
                    action.payload.layoutIdxA,
                    action.payload.layoutIdxB,
                    state.schematizedLayouts || []
                ),
            }

        // Ui actions
        case SCHEMATIZED_CANCEL_BUTTON_CLICKED:
            return {
                ...state,
                schematizedLayouts: state.oldSchematizedLayouts,
                size: getFirstSelectedLayoutSize(state.oldSchematizedLayouts),
                sizeChanged: false,
            }

        case SCHEMATIZED_GRID_SELECTED:
            return { ...state, selectedGridId: action.payload.gridId }

        case SCHEMATIZED_LAYOUT_SELECTED:
            return {
                ...state,
                layoutRenameInputValue: null,
                selectedLayoutId: action.payload.layoutId,
                selectedGridId: null,
            }

        case SCHEMATIZED_SEARCH_CLICKED: {
            return { ...state, searchKey: action.payload.searchKey }
        }

        case SCHEMATIZED_RESOLUTION_CHANGED: {
            return { ...state, sizeChanged: state.size !== action.payload.size, size: action.payload.size }
        }

        case SCHEMATIZED_SAVE_SUCCESS: {
            const newLayouts = state.schematizedLayouts.map(l => ({
                ...l,
                // Just dirty fix, we should not store the size per layout at all
                // and query/mutate is from api as separate schema property

                size: state.size,
            }))
            return { ...state, sizeChanged: false, schematizedLayouts: newLayouts, oldSchematizedLayouts: newLayouts }
        }

        case SCHEMATIZED_LAYOUT_RENAME_VALUE_CHANGED: {
            return { ...state, layoutRenameInputValue: action.payload.value }
        }

        case SCHEMATIZED_LAYOUT_RENAME_RESET: {
            return { ...state, layoutRenameInputValue: null }
        }

        case FETCH_SCHEMATIZED_DATA_REQUESTED:
            return createDefaultSchematizedState()

        case RESET_SCHEMATIZED_STATE:
            return { ...state, sizeChanged: false, oldSchematizedLayouts: state.schematizedLayouts }

        default:
            return state
    }
}

const updateLayoutById = (
    layoutId: Maybe<string>,
    layouts: SchematizedData[],
    getNewLayoutData: (layoutData: SchematizedData) => SchematizedData
): SchematizedData[] => {
    return layouts.map(l => {
        if (layoutId && l.layoutId === layoutId) {
            return getNewLayoutData(l)
        }

        return l
    })
}

const getFirstSelectedLayoutSize = (data: SchematizedData[]): SchematizedSize => {
    const firstLayout = head(data)
    return firstLayout ? firstLayout.size : SchematizedSize.Medium
}

export const reorderLayoutsSortIndex = (
    layoutIdxA: number,
    layoutIdxB: number,
    data: SchematizedData[]
): SchematizedData[] => {
    const [layoutA, layoutB] = [data[layoutIdxA], data[layoutIdxB]]

    if (!layoutA || !layoutB) {
        return data
    }

    return data.map(l => ({
        ...l,
        sortIndex:
            l.layoutId === layoutA.layoutId ? layoutIdxB : l.layoutId === layoutB.layoutId ? layoutIdxA : l.sortIndex,
    }))
}
